import React from "react";

/**
 * High-fidelity vector SVG logos for companies whose favicons
 * are blocked, low-res, or fail to load via public favicon scrapers.
 */

export function BmwLogo({ className = "h-6 w-6 sm:h-7 sm:w-7" }) {
  return (
    <svg
      viewBox="0 0 100 100"
      className={`${className} object-contain`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="BMW logo"
    >
      {/* Outer chrome bezel */}
      <circle cx="50" cy="50" r="48" fill="#E2E8F0" stroke="#CBD5E1" strokeWidth="1" />
      
      {/* Outer rich black ring */}
      <circle cx="50" cy="50" r="45" fill="#18181B" />
      
      {/* Inner dividing chrome border */}
      <circle cx="50" cy="50" r="26.5" fill="#E2E8F0" />
      
      {/* Center 4 quadrants (Bavarian blue & pure white) */}
      {/* Top-Left Quadrant: Blue */}
      <path d="M 50 50 L 25 50 A 25 25 0 0 1 50 25 Z" fill="#0066B1" />
      {/* Top-Right Quadrant: White */}
      <path d="M 50 50 L 50 25 A 25 25 0 0 1 75 50 Z" fill="#FFFFFF" />
      {/* Bottom-Right Quadrant: Blue */}
      <path d="M 50 50 L 75 50 A 25 25 0 0 1 50 75 Z" fill="#0066B1" />
      {/* Bottom-Left Quadrant: White */}
      <path d="M 50 50 L 50 75 A 25 25 0 0 1 25 50 Z" fill="#FFFFFF" />
      
      {/* Inner quadrants dividing hairline */}
      <line x1="25" y1="50" x2="75" y2="50" stroke="#CBD5E1" strokeWidth="0.8" />
      <line x1="50" y1="25" x2="50" y2="75" stroke="#CBD5E1" strokeWidth="0.8" />
      
      {/* Curved BMW Lettering on the black ring */}
      {/* M at 12 o'clock */}
      <text
        x="50"
        y="18"
        fill="#FFFFFF"
        fontSize="12.5"
        fontWeight="800"
        fontFamily="Arial, Helvetica, -apple-system, sans-serif"
        textAnchor="middle"
      >
        M
      </text>
      
      {/* B at ~10 o'clock */}
      <text
        x="26.5"
        y="28.5"
        fill="#FFFFFF"
        fontSize="12.5"
        fontWeight="800"
        fontFamily="Arial, Helvetica, -apple-system, sans-serif"
        textAnchor="middle"
        transform="rotate(-48 26.5 28.5)"
      >
        B
      </text>
      
      {/* W at ~2 o'clock */}
      <text
        x="73.5"
        y="28.5"
        fill="#FFFFFF"
        fontSize="12.5"
        fontWeight="800"
        fontFamily="Arial, Helvetica, -apple-system, sans-serif"
        textAnchor="middle"
        transform="rotate(48 73.5 28.5)"
      >
        W
      </text>
    </svg>
  );
}

export function McKinseyLogo({ className = "h-6 w-6 sm:h-7 sm:w-7" }) {
  return (
    <svg
      viewBox="0 0 100 100"
      className={`${className} object-contain rounded-md`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="McKinsey & Company logo"
    >
      {/* McKinsey Signature Deep Midnight Navy Background */}
      <rect width="100" height="100" rx="18" fill="#051C2C" />
      
      {/* Elegant Serif McKinsey Monogram typography */}
      <text
        x="50"
        y="56"
        fill="#FFFFFF"
        fontSize="32"
        fontWeight="bold"
        fontFamily="'Georgia', 'Times New Roman', serif"
        textAnchor="middle"
        letterSpacing="0.5"
      >
        McK
      </text>

      {/* Sub-label '& Co' in refined typography */}
      <text
        x="50"
        y="75"
        fill="#94A3B8"
        fontSize="13"
        fontWeight="600"
        fontFamily="'Arial', sans-serif"
        textAnchor="middle"
        letterSpacing="1"
      >
        & COMPANY
      </text>

      {/* Sleek bottom accent line */}
      <rect x="22" y="83" width="56" height="2.5" rx="1.2" fill="#205493" />
    </svg>
  );
}

export function TcsLogo({ className = "h-6 w-6 sm:h-7 sm:w-7" }) {
  return (
    <svg
      viewBox="0 0 100 100"
      className={`${className} object-contain rounded-md`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Tata Consultancy Services logo"
    >
      <defs>
        <linearGradient id="tcs-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0060B2" />
          <stop offset="100%" stopColor="#00356B" />
        </linearGradient>
      </defs>

      {/* Tata Corporate Blue Rounded Background */}
      <rect width="100" height="100" rx="18" fill="url(#tcs-grad)" />

      {/* Iconic Tata 'T' Trustmark Symbol */}
      <g transform="translate(18.4, 10) scale(0.14)" fill="#FFFFFF">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M375.394,93.432c-3.794-7.461-8.791-14.612-15.007-21.318c-13.969-15.077-33.678-27.623-57.002-36.276c-23.516-8.72-50.338-13.334-77.595-13.334s-54.079,4.614-77.583,13.334c-23.336,8.654-43.046,21.2-57.015,36.276C84.98,78.819,79.967,85.98,76.174,93.445c30.365-7.343,82.306-17.038,130.534-18.063c4.652-0.1,7.855,1.39,9.964,4.063c2.569,3.255,2.376,14.858,2.313,20.049l-1.371,134.17c2.716,0.09,5.445,0.148,8.176,0.148c2.754,0,5.498-0.045,8.214-0.135l-1.371-134.184c-0.071-5.19-0.27-16.794,2.308-20.049c2.114-2.672,5.306-4.162,9.957-4.063C293.106,76.398,345.035,86.095,375.394,93.432"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M381.877,110.88c-44.442-10.047-74.121-11.905-103.405-13.53c-25.526-1.419-25.859,7.696-23.311,25.115c0.167,1.063,0.372,2.403,0.615,3.931c8.562,50.712,19.224,94.352,21.056,101.759c62.23-14.225,107.109-53.664,107.109-99.994C383.941,122.327,383.236,116.547,381.877,110.88"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M196.443,122.465c2.551-17.418,2.224-26.534-23.299-25.115c-29.294,1.625-58.98,3.482-103.439,13.536c-1.358,5.667-2.072,11.44-2.072,17.274c0,20.024,8.146,39.4,23.56,56.047c13.969,15.077,33.679,27.623,57.015,36.282c8.446,3.127,17.359,5.69,26.54,7.74c1.723-6.939,12.63-51.487,21.285-103.106C196.193,124.134,196.341,123.221,196.443,122.465"
        />
      </g>

      {/* TCS Bold Typography */}
      <text
        x="50"
        y="78"
        fill="#FFFFFF"
        fontSize="21"
        fontWeight="800"
        fontFamily="Arial, Helvetica, -apple-system, sans-serif"
        textAnchor="middle"
        letterSpacing="2.5"
      >
        TCS
      </text>

      {/* Subtle bottom accent line */}
      <rect x="26" y="87" width="48" height="2" rx="1" fill="#60A5FA" opacity="0.85" />
    </svg>
  );
}

// Map company id to custom vector SVG logo component
export const COMPANY_CUSTOM_LOGOS = {
  bmw: BmwLogo,
  mckinsey: McKinseyLogo,
  tcs: TcsLogo,
};
